import React from "react";
import {useParams} from "react-router";
import {css} from "@emotion/react";

import {HomeIcon} from "@pg-design/icons-module";
import {omit, upperFirst} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../app/components/breadcrumbs/BreadcrumbsItem";
import {IBreadcrumb} from "../../app/components/breadcrumbs/IBreadcrumb";
import {getAbroadOfferAndPropertyBreadcrumbs} from "../../investment_offer/utils/get_abroad_offer_and_property_breadcrumbs";
import {Country} from "../../region/types/Country";
import {useUserPreferencesQueryParams} from "../../user/hooks/use_user_preferences_query_params";
import {getUserPreferencesToOmit} from "../../user/utils/get_user_preferences_to_omit";
import {IOfferGroupsSimplified} from "../detail/types/IOfferGroups";
import {createOfferLink} from "../helpers/create_offer_link";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {buildSpecialListingLink, ListingNameType} from "../helpers/friendly_offer_list/build_special_listing_link";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {getOfferTypeNamePluralCapital} from "../helpers/OfferType";
import {IRegionAncestor} from "../list/actions/offer_list_selected_data_actions";
import {getCountryId, getCountryName} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {getFloorChoiceMetaText} from "../list/helpers/sub_type/meta/get_floor_choice_meta_text";
import {getHouseFilterMetaText} from "../list/helpers/sub_type/meta/get_house_filter_meta_text";
import {getOfferListSubTypeLabel} from "../list/helpers/sub_type/offer_list_sub_type";
import {isOfferListSubTypeValidFloorChoice} from "../list/helpers/sub_type/validator/is_offer_list_sub_type_valid_floor_choice";
import {isOfferListSubTypeValidHouseFilter} from "../list/helpers/sub_type/validator/is_offer_list_sub_type_valid_house_filter";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {OfferType} from "../types/OfferType";

interface IBreadcrumbLinkData {
    name: string;
    slug: string;
    id: number;
}

interface IBreadcrumbLinkPropertyData {
    id: number;
    number: string;
}

interface IProps {
    breadcrumbs: {
        region_type_voivodeship: IRegionAncestor | null;
        region_type_city: IRegionAncestor | null;
        region_type_county: IRegionAncestor | null;
        region_type_district: IRegionAncestor | null;
        region_type_housing_estate: IRegionAncestor | null;
        region_type_town: IRegionAncestor | null;
    };
    country?: string;
    type: OfferType;
    vendor?: IBreadcrumbLinkData;
    offer?: IBreadcrumbLinkData;
    property?: IBreadcrumbLinkPropertyData;
    groups?: IOfferGroupsSimplified | null;
    specialListing?: string;
}

export const OfferAndPropertyBreadcrumbs = React.memo((props: IProps) => {
    const params = useParams<{friendlySlug?: string; offerListSubType?: OfferListSubType; offerListSubFilter?: string}>();
    const userPreferencesQueryParams = useUserPreferencesQueryParams();

    const {type, breadcrumbs, groups, property, offer, vendor, specialListing} = props;
    const offerTypeName = type != null ? getOfferTypeNamePluralCapital(type) : "Oferty";
    const clearedPreferences = omit(userPreferencesQueryParams, getUserPreferencesToOmit(type));

    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} = breadcrumbs;
    const regionsList = [region_type_voivodeship, region_type_county, region_type_city, region_type_district, region_type_housing_estate, region_type_town];
    const country = getCountryId(props.country);

    const isAbroadRegion = country === Country.SPAIN && regionsList[0]?.id !== null;

    const allBreadcrumbsData: IBreadcrumb[] =
        !props.country || country === Country.POLAND || isAbroadRegion
            ? [
                  {
                      name: offerTypeName,
                      position: 2,
                      url: buildFriendlyOfferListLink({type, ...clearedPreferences}),
                      key: "offer-type"
                  },
                  ...(isAbroadRegion ? getCountryBreadcrumb(type, country, clearedPreferences) : []),
                  ...getRegionBreadcrumbs(regionsList, type, country, clearedPreferences),
                  ...getSpecialListingBreadcrumbs(breadcrumbs, type, specialListing, regionsList.length, clearedPreferences),
                  // there is no list sub types, uncomment when needed
                  //...getOfferListSubTypeBreadcrumbs(params, regionsList.length),
                  ...getVendorBreadcrumbs(vendor, regionsList.length),
                  ...getOfferBreadcrumbs(vendor, offer, regionsList.length, groups),
                  ...getPropertyBreadcrumbs(vendor, offer, property, regionsList.length),
                  ...getListingSubFilterBreadcrumbs(params, regionsList.length)
              ]
            : [
                  ...getAbroadOfferAndPropertyBreadcrumbs(country),
                  ...getOfferBreadcrumbs(vendor, offer, regionsList.length, groups),
                  ...getPropertyBreadcrumbs(vendor, offer, property, regionsList.length)
              ];

    return (
        <Breadcrumbs homePageUrl={rpAppLink.base()} homePageIcon={<HomeIcon size="1.4" css={iconStyle} />}>
            {allBreadcrumbsData.map((breadcrumb, index) => {
                const isLastItem = allBreadcrumbsData.length === index + 1;

                return <BreadcrumbsItem {...breadcrumb} key={breadcrumb.key} isLinkingToCurrentLocation={isLastItem} />;
            })}
        </Breadcrumbs>
    );
});

const iconStyle = css`
    margin: 0 0.4rem 0 0.2rem;
`;

const getRegionBreadcrumbs = (
    regionList: (IRegionAncestor | null)[],
    type: OfferType,
    country?: Country,
    userPreferencesQueryParams?: Record<string, string | number | string[]>
) => {
    return regionList.reduce<IBreadcrumb[]>((acc, region, index) => {
        if (region === null) {
            return acc;
        }
        const isAbroadRegion = country !== Country.POLAND;

        const link = isAbroadRegion
            ? (() => {
                  const {friendlySlug, countrySlug} = offerUrlBuilder({type, region: region.slug, country: country});
                  const baseLink = rpAppLink.offer.listFriendlyAbroad({
                      friendlySlug: friendlySlug,
                      abroadRegionSlug: region.slug,
                      country: countrySlug
                  });
                  return userPreferencesQueryParams ? appendQueryString(baseLink, userPreferencesQueryParams) : baseLink;
              })()
            : buildFriendlyOfferListLink({type, region: region.slug, ...userPreferencesQueryParams});

        return [
            ...acc,
            {
                key: index.toString(),
                name: region.name,
                position: index + 3,
                url: link
            }
        ];
    }, []);
};

const getCountryBreadcrumb = (type: OfferType, country: Country, userPreferencesQueryParams?: Record<string, string | number | string[]>) => {
    const regionFriendlySlug = offerUrlBuilder({type, region: ""}).friendlySlug;
    const countryName = getCountryName(country);
    const baseLink = rpAppLink.offer.listFriendlyAbroad({friendlySlug: regionFriendlySlug, country: countryName});
    // For abroad regions, user preferences are added as query string
    const link = userPreferencesQueryParams ? appendQueryString(baseLink, userPreferencesQueryParams) : baseLink;

    return [
        {
            key: countryName,
            name: upperFirst(countryName),
            position: 3,
            url: link
        }
    ];
};

const getOfferBreadcrumbs = (
    vendor: IBreadcrumbLinkData | undefined,
    offer: IBreadcrumbLinkData | undefined,
    regionListCount: number,
    groups: IOfferGroupsSimplified | null | undefined
) => {
    if (!vendor || !offer) {
        return [];
    }

    const offerLink = createOfferLink({
        ...offer,
        vendor: {slug: vendor.slug},
        groups: groups || null
    });

    return [
        {
            key: "offer-" + offer.name,
            name: offer.name,
            position: regionListCount + 2,
            url: offerLink
        }
    ];
};

const getPropertyBreadcrumbs = (
    vendor: IBreadcrumbLinkData | undefined,
    offer: IBreadcrumbLinkData | undefined,
    property: IBreadcrumbLinkPropertyData | undefined,
    regionListCount: number
) => {
    if (!vendor || !property || !offer) {
        return [];
    }
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    return [
        {
            key: "property-" + property.number,
            name: `Mieszkanie: ${property.number}`,
            position: regionListCount + 3,
            url: propertyLink
        }
    ];
};

const getSpecialListingBreadcrumbs = (
    breadcrumbs: {
        region_type_voivodeship: IRegionAncestor | null;
        region_type_city: IRegionAncestor | null;
        region_type_county: IRegionAncestor | null;
        region_type_district: IRegionAncestor | null;
        region_type_housing_estate: IRegionAncestor | null;
        region_type_town: IRegionAncestor | null;
    },
    type: OfferType,
    specialListing: string | undefined,
    regionsListCount: number,
    userPreferencesQueryParams?: Record<string, string | number | string[]>
) => {
    if (!specialListing) {
        return [];
    }
    const listingName = specialListing.split("-")[0] as ListingNameType;
    const friendlySlug = buildSpecialListingLink(listingName, type, breadcrumbs);
    const listingLink = rpAppLink.offer.listFriendly({friendlySlug});

    return [
        {
            key: "listing-special" + (regionsListCount + 1),
            name: listingName,
            position: regionsListCount + 1,
            url: userPreferencesQueryParams ? appendQueryString(listingLink, userPreferencesQueryParams) : listingLink
        }
    ];
};

const getVendorBreadcrumbs = (vendor: IBreadcrumbLinkData | undefined, regionsListCount: number) => {
    if (!vendor) {
        return [];
    }

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: vendor.slug, vendorId: vendor.id});

    return [
        {
            key: "vendor-" + vendor.name,
            name: vendor.name,
            position: regionsListCount + 1,
            url: vendorLink
        }
    ];
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getOfferListSubTypeBreadcrumbs = (params: {offerListSubType?: OfferListSubType}, regionsListCount: number) => {
    if (!params.offerListSubType) {
        return [];
    }

    return [
        {
            key: "offer-list-type-" + (regionsListCount + 1),
            name: getOfferListSubTypeLabel(params.offerListSubType),
            position: regionsListCount + 1
        }
    ];
};

const getListingSubFilterBreadcrumbs = (params: {offerListSubFilter?: string; friendlySlug?: string}, regionsListCount: number) => {
    const {offerListSubFilter} = params;
    if (offerListSubFilter && isOfferListSubTypeValidFloorChoice(offerListSubFilter)) {
        return [
            {
                key: `sub-filter-${offerListSubFilter}`,
                name: getFloorChoiceMetaText(offerListSubFilter),
                position: regionsListCount + 1
            }
        ];
    }

    if (offerListSubFilter && isOfferListSubTypeValidHouseFilter(offerListSubFilter)) {
        return [
            {
                key: `sub-filter-${offerListSubFilter}`,
                name: getHouseFilterMetaText(offerListSubFilter),
                position: regionsListCount + 1
            }
        ];
    }

    const parsedParams = params.friendlySlug ? offerUrlParser(params.friendlySlug, params?.offerListSubFilter) : null;
    const hasRoomsSameValue = parsedParams?.rooms_1 && parsedParams.rooms_1 === parsedParams.rooms_0;
    const isSingleRoom = !parsedParams?.rooms_0 && parsedParams?.rooms_1 === 1;
    const roomCount = parsedParams?.rooms_1 || parsedParams?.rooms_0;
    const isFiveRooms = !parsedParams?.rooms_1 && parsedParams?.rooms_0 === 5;
    if ((isSingleRoom || hasRoomsSameValue || isFiveRooms) && roomCount) {
        const rooms = {
            1: parsedParams?.type === OfferType.FLAT ? "kawalerki" : "jednopokojowe",
            2: "dwupokojowe",
            3: "trzypokojowe",
            4: "czteropokojowe",
            5: "pięciopokojowe i więcej"
        } as Record<number, string>;
        return [
            {
                key: "rooms",
                name: rooms[roomCount],
                position: regionsListCount + 1
            }
        ];
    }

    const lowerPrice = parsedParams?.price_0;
    const upperPrice = parsedParams?.price_1;
    if ((!lowerPrice && upperPrice) || (!upperPrice && lowerPrice)) {
        const priceToDivide = (upperPrice || lowerPrice) as number;
        const roundedThousands = Math.ceil(priceToDivide / 1000);
        const prefix = upperPrice ? "Do" : "Od";

        let outputPrice = 0;
        let suffix: "tys" | "mln" = "tys";
        if (roundedThousands < 1000) {
            outputPrice = roundedThousands;
        } else if (roundedThousands === 1000) {
            outputPrice = 1;
            suffix = "mln";
        } else {
            outputPrice = parseFloat((roundedThousands / 1000).toFixed(3).replace(/\.?0+$/, ""));
            suffix = "mln";
        }

        return [
            {
                key: "price",
                name: `${prefix} ${outputPrice} ${suffix}`,
                position: regionsListCount + 1
            }
        ];
    }

    return [];
};
import React from "react";
import {useParams} from "react-router";
import {css} from "@emotion/react";

import {HomeIcon} from "@pg-design/icons-module";
import {omit, upperFirst} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../app/components/breadcrumbs/BreadcrumbsItem";
import {IBreadcrumb} from "../../app/components/breadcrumbs/IBreadcrumb";
import {getAbroadOfferAndPropertyBreadcrumbs} from "../../investment_offer/utils/get_abroad_offer_and_property_breadcrumbs";
import {Country} from "../../region/types/Country";
import {useUserPreferencesQueryParams} from "../../user/hooks/use_user_preferences_query_params";
import {getUserPreferencesToOmit} from "../../user/utils/get_user_preferences_to_omit";
import {IOfferGroupsSimplified} from "../detail/types/IOfferGroups";
import {createOfferLink} from "../helpers/create_offer_link";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {buildSpecialListingLink, ListingNameType} from "../helpers/friendly_offer_list/build_special_listing_link";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {getOfferTypeNamePluralCapital} from "../helpers/OfferType";
import {IRegionAncestor} from "../list/actions/offer_list_selected_data_actions";
import {getCountryId, getCountryName} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {getFloorChoiceMetaText} from "../list/helpers/sub_type/meta/get_floor_choice_meta_text";
import {getHouseFilterMetaText} from "../list/helpers/sub_type/meta/get_house_filter_meta_text";
import {getOfferListSubTypeLabel} from "../list/helpers/sub_type/offer_list_sub_type";
import {isOfferListSubTypeValidFloorChoice} from "../list/helpers/sub_type/validator/is_offer_list_sub_type_valid_floor_choice";
import {isOfferListSubTypeValidHouseFilter} from "../list/helpers/sub_type/validator/is_offer_list_sub_type_valid_house_filter";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {OfferType} from "../types/OfferType";

interface IBreadcrumbLinkData {
    name: string;
    slug: string;
    id: number;
}

interface IBreadcrumbLinkPropertyData {
    id: number;
    number: string;
}

interface IProps {
    breadcrumbs: {
        region_type_voivodeship: IRegionAncestor | null;
        region_type_city: IRegionAncestor | null;
        region_type_county: IRegionAncestor | null;
        region_type_district: IRegionAncestor | null;
        region_type_housing_estate: IRegionAncestor | null;
        region_type_town: IRegionAncestor | null;
    };
    country?: string;
    type: OfferType;
    vendor?: IBreadcrumbLinkData;
    offer?: IBreadcrumbLinkData;
    property?: IBreadcrumbLinkPropertyData;
    groups?: IOfferGroupsSimplified | null;
    specialListing?: string;
}

export const OfferAndPropertyBreadcrumbs = React.memo((props: IProps) => {
    const params = useParams<{friendlySlug?: string; offerListSubType?: OfferListSubType; offerListSubFilter?: string}>();
    const userPreferencesQueryParams = useUserPreferencesQueryParams();

    const {type, breadcrumbs, groups, property, offer, vendor, specialListing} = props;
    const offerTypeName = type != null ? getOfferTypeNamePluralCapital(type) : "Oferty";
    const clearedPreferences = omit(userPreferencesQueryParams, getUserPreferencesToOmit(type));

    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} = breadcrumbs;
    const regionsList = [region_type_voivodeship, region_type_county, region_type_city, region_type_district, region_type_housing_estate, region_type_town];
    const country = getCountryId(props.country);

    const isAbroadRegion = country === Country.SPAIN && regionsList[0]?.id !== null;

    const allBreadcrumbsData: IBreadcrumb[] =
        !props.country || country === Country.POLAND || isAbroadRegion
            ? [
                  {
                      name: offerTypeName,
                      position: 2,
                      url: buildFriendlyOfferListLink({type, ...clearedPreferences}),
                      key: "offer-type"
                  },
                  ...(isAbroadRegion ? getCountryBreadcrumb(type, country, clearedPreferences) : []),
                  ...getRegionBreadcrumbs(regionsList, type, country, clearedPreferences),
                  ...getSpecialListingBreadcrumbs(breadcrumbs, type, specialListing, regionsList.length, clearedPreferences),
                  // there is no list sub types, uncomment when needed
                  //...getOfferListSubTypeBreadcrumbs(params, regionsList.length),
                  ...getVendorBreadcrumbs(vendor, regionsList.length),
                  ...getOfferBreadcrumbs(vendor, offer, regionsList.length, groups),
                  ...getPropertyBreadcrumbs(vendor, offer, property, regionsList.length),
                  ...getListingSubFilterBreadcrumbs(params, regionsList.length)
              ]
            : [
                  ...getAbroadOfferAndPropertyBreadcrumbs(country),
                  ...getOfferBreadcrumbs(vendor, offer, regionsList.length, groups),
                  ...getPropertyBreadcrumbs(vendor, offer, property, regionsList.length)
              ];

    return (
        <Breadcrumbs homePageUrl={rpAppLink.base()} homePageIcon={<HomeIcon size="1.4" css={iconStyle} />}>
            {allBreadcrumbsData.map((breadcrumb, index) => {
                const isLastItem = allBreadcrumbsData.length === index + 1;

                return <BreadcrumbsItem {...breadcrumb} key={breadcrumb.key} isLinkingToCurrentLocation={isLastItem} />;
            })}
        </Breadcrumbs>
    );
});

const iconStyle = css`
    margin: 0 0.4rem 0 0.2rem;
`;

const getRegionBreadcrumbs = (
    regionList: (IRegionAncestor | null)[],
    type: OfferType,
    country?: Country,
    userPreferencesQueryParams?: Record<string, string | number | string[]>
) => {
    return regionList.reduce<IBreadcrumb[]>((acc, region, index) => {
        if (region === null) {
            return acc;
        }
        const isAbroadRegion = country !== Country.POLAND;

        const link = isAbroadRegion
            ? (() => {
                  const {friendlySlug, countrySlug} = offerUrlBuilder({type, region: region.slug, country: country});
                  const baseLink = rpAppLink.offer.listFriendlyAbroad({
                      friendlySlug: friendlySlug,
                      abroadRegionSlug: region.slug,
                      country: countrySlug
                  });
                  return userPreferencesQueryParams ? appendQueryString(baseLink, userPreferencesQueryParams) : baseLink;
              })()
            : buildFriendlyOfferListLink({type, region: region.slug, ...userPreferencesQueryParams});

        return [
            ...acc,
            {
                key: index.toString(),
                name: region.name,
                position: index + 3,
                url: link
            }
        ];
    }, []);
};

const getCountryBreadcrumb = (type: OfferType, country: Country, userPreferencesQueryParams?: Record<string, string | number | string[]>) => {
    const regionFriendlySlug = offerUrlBuilder({type, region: ""}).friendlySlug;
    const countryName = getCountryName(country);
    const baseLink = rpAppLink.offer.listFriendlyAbroad({friendlySlug: regionFriendlySlug, country: countryName});
    // For abroad regions, user preferences are added as query string
    const link = userPreferencesQueryParams ? appendQueryString(baseLink, userPreferencesQueryParams) : baseLink;

    return [
        {
            key: countryName,
            name: upperFirst(countryName),
            position: 3,
            url: link
        }
    ];
};

const getOfferBreadcrumbs = (
    vendor: IBreadcrumbLinkData | undefined,
    offer: IBreadcrumbLinkData | undefined,
    regionListCount: number,
    groups: IOfferGroupsSimplified | null | undefined
) => {
    if (!vendor || !offer) {
        return [];
    }

    const offerLink = createOfferLink({
        ...offer,
        vendor: {slug: vendor.slug},
        groups: groups || null
    });

    return [
        {
            key: "offer-" + offer.name,
            name: offer.name,
            position: regionListCount + 2,
            url: offerLink
        }
    ];
};

const getPropertyBreadcrumbs = (
    vendor: IBreadcrumbLinkData | undefined,
    offer: IBreadcrumbLinkData | undefined,
    property: IBreadcrumbLinkPropertyData | undefined,
    regionListCount: number
) => {
    if (!vendor || !property || !offer) {
        return [];
    }
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    return [
        {
            key: "property-" + property.number,
            name: `Mieszkanie: ${property.number}`,
            position: regionListCount + 3,
            url: propertyLink
        }
    ];
};

const getSpecialListingBreadcrumbs = (
    breadcrumbs: {
        region_type_voivodeship: IRegionAncestor | null;
        region_type_city: IRegionAncestor | null;
        region_type_county: IRegionAncestor | null;
        region_type_district: IRegionAncestor | null;
        region_type_housing_estate: IRegionAncestor | null;
        region_type_town: IRegionAncestor | null;
    },
    type: OfferType,
    specialListing: string | undefined,
    regionsListCount: number,
    userPreferencesQueryParams?: Record<string, string | number | string[]>
) => {
    if (!specialListing) {
        return [];
    }
    const listingName = specialListing.split("-")[0] as ListingNameType;
    const friendlySlug = buildSpecialListingLink(listingName, type, breadcrumbs);
    const listingLink = rpAppLink.offer.listFriendly({friendlySlug});

    return [
        {
            key: "listing-special" + (regionsListCount + 1),
            name: listingName,
            position: regionsListCount + 1,
            url: userPreferencesQueryParams ? appendQueryString(listingLink, userPreferencesQueryParams) : listingLink
        }
    ];
};

const getVendorBreadcrumbs = (vendor: IBreadcrumbLinkData | undefined, regionsListCount: number) => {
    if (!vendor) {
        return [];
    }

    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: vendor.slug, vendorId: vendor.id});

    return [
        {
            key: "vendor-" + vendor.name,
            name: vendor.name,
            position: regionsListCount + 1,
            url: vendorLink
        }
    ];
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getOfferListSubTypeBreadcrumbs = (params: {offerListSubType?: OfferListSubType}, regionsListCount: number) => {
    if (!params.offerListSubType) {
        return [];
    }

    return [
        {
            key: "offer-list-type-" + (regionsListCount + 1),
            name: getOfferListSubTypeLabel(params.offerListSubType),
            position: regionsListCount + 1
        }
    ];
};

const getListingSubFilterBreadcrumbs = (params: {offerListSubFilter?: string; friendlySlug?: string}, regionsListCount: number) => {
    const {offerListSubFilter} = params;
    if (offerListSubFilter && isOfferListSubTypeValidFloorChoice(offerListSubFilter)) {
        return [
            {
                key: `sub-filter-${offerListSubFilter}`,
                name: getFloorChoiceMetaText(offerListSubFilter),
                position: regionsListCount + 1
            }
        ];
    }

    if (offerListSubFilter && isOfferListSubTypeValidHouseFilter(offerListSubFilter)) {
        return [
            {
                key: `sub-filter-${offerListSubFilter}`,
                name: getHouseFilterMetaText(offerListSubFilter),
                position: regionsListCount + 1
            }
        ];
    }

    const parsedParams = params.friendlySlug ? offerUrlParser(params.friendlySlug, params?.offerListSubFilter) : null;
    const hasRoomsSameValue = parsedParams?.rooms_1 && parsedParams.rooms_1 === parsedParams.rooms_0;
    const isSingleRoom = !parsedParams?.rooms_0 && parsedParams?.rooms_1 === 1;
    const roomCount = parsedParams?.rooms_1 || parsedParams?.rooms_0;
    const isFiveRooms = !parsedParams?.rooms_1 && parsedParams?.rooms_0 === 5;
    if ((isSingleRoom || hasRoomsSameValue || isFiveRooms) && roomCount) {
        const rooms = {
            1: parsedParams?.type === OfferType.FLAT ? "kawalerki" : "jednopokojowe",
            2: "dwupokojowe",
            3: "trzypokojowe",
            4: "czteropokojowe",
            5: "pięciopokojowe i więcej"
        } as Record<number, string>;
        return [
            {
                key: "rooms",
                name: rooms[roomCount],
                position: regionsListCount + 1
            }
        ];
    }

    const lowerPrice = parsedParams?.price_0;
    const upperPrice = parsedParams?.price_1;
    if ((!lowerPrice && upperPrice) || (!upperPrice && lowerPrice)) {
        const priceToDivide = (upperPrice || lowerPrice) as number;
        const roundedThousands = Math.ceil(priceToDivide / 1000);
        const prefix = upperPrice ? "Do" : "Od";

        let outputPrice = 0;
        let suffix: "tys" | "mln" = "tys";
        if (roundedThousands < 1000) {
            outputPrice = roundedThousands;
        } else if (roundedThousands === 1000) {
            outputPrice = 1;
            suffix = "mln";
        } else {
            outputPrice = parseFloat((roundedThousands / 1000).toFixed(3).replace(/\.?0+$/, ""));
            suffix = "mln";
        }

        return [
            {
                key: "price",
                name: `${prefix} ${outputPrice} ${suffix}`,
                position: regionsListCount + 1
            }
        ];
    }

    return [];
};
